// services/ambientAudioManager.js
// Event-driven ambient clip player (hot mic, ad libs, producer fill)
// No timers - PlayerContext calls playClip() at the right moments

import api from './api';
import logger from './logger';

class AmbientAudioManager {
  constructor() {
    this._suppressed = false;  // True when DJ break or show open is playing
    this._musicPlaying = false; // True when Spotify is actively playing music
    this._audioElement = null; // Dedicated Audio element for overlay playback
    this._modules = [];        // Ambient modules from backend
    this._lastPlayEndTime = 0; // Timestamp when last clip finished (cross-module cooldown)
    this._playing = false;     // Sync lock to prevent concurrent playClip() races
    this._fadeVolume = null;   // Callback to duck music volume (set by PlayerContext)
  }

  /**
   * Register the fadeVolume callback from PlayerContext so ambient clips can duck music
   */
  setFadeVolume(fadeVolumeFn) {
    this._fadeVolume = fadeVolumeFn;
  }

  /**
   * Suppress ambient playback (called by PlayerContext during DJ breaks/show opens)
   */
  suppress() {
    this._suppressed = true;
    logger.debug('Ambient suppressed (DJ speaking)');
  }

  /**
   * Unsuppress ambient playback (called by PlayerContext when DJ finishes)
   */
  unsuppress() {
    this._suppressed = false;
    logger.debug('Ambient unsuppressed (DJ done)');
  }

  /**
   * Update music playing state (called by PlayerContext on play/pause)
   * Ambient clips only play when music is actively playing
   */
  setMusicPlaying(isPlaying) {
    this._musicPlaying = isPlaying;
  }

  /**
   * Initialize with available modules (no timers, just stores module list)
   * @param {Array} modules - Full module list from GET /api/modules
   */
  init(modules) {
    this._suppressed = false;
    this._lastPlayEndTime = 0;
    this._playing = false;

    // Filter to ambient pool modules only
    this._modules = (modules || []).filter(
      m => m.category === 'pool' && m.subcategory === 'ambient'
    );

    if (this._modules.length === 0) {
      logger.debug('No ambient modules available');
      return;
    }

    logger.debug(`Ambient manager initialized with ${this._modules.length} modules: ${this._modules.map(m => m.id).join(', ')}`);
  }

  /**
   * Stop ambient playback and clear state
   */
  stop() {
    this._suppressed = false;
    this._playing = false;

    // Stop any playing audio
    if (this._audioElement) {
      this._audioElement.pause();
      this._audioElement = null;
    }

    // Restore music volume in case we were mid-duck
    if (this._fadeVolume) this._fadeVolume(1.0, 300);

    this._modules = [];
    this._lastPlayEndTime = 0;
    logger.debug('Ambient manager stopped');
  }

  /**
   * Play a clip from the given module. Called by PlayerContext at event-driven moments.
   * Guards: suppressed, music paused, overlap, 20s cooldown.
   * @param {string} moduleId - e.g. 'ad_libs', 'hot_mic', 'producer_fill'
   * @returns {Promise<boolean>} true if clip played, false if skipped
   */
  async playClip(moduleId) {
    // Guard: suppressed (DJ speaking)
    if (this._suppressed) {
      logger.debug(`Ambient skipped ${moduleId} (suppressed)`);
      return false;
    }

    // Guard: music not playing
    if (!this._musicPlaying) {
      logger.debug(`Ambient skipped ${moduleId} (music paused)`);
      return false;
    }

    // Guard: sync lock — prevents concurrent calls from racing past the async fetch
    if (this._playing) {
      logger.debug(`Ambient skipped ${moduleId} (already playing/loading)`);
      return false;
    }

    // Guard: 20s cross-module cooldown
    const timeSinceLastPlay = Date.now() - this._lastPlayEndTime;
    if (moduleId !== 'producer_fill' && this._lastPlayEndTime > 0 && timeSinceLastPlay < 20000) {
      logger.debug(`Ambient skipped ${moduleId} (cooldown, ${Math.round(timeSinceLastPlay / 1000)}s ago)`);
      return false;
    }

    // Acquire lock BEFORE async fetch
    this._playing = true;

    // Find the module config for volume
    const mod = this._modules.find(m => m.id === moduleId);
    const clipVolume = mod?.pool_config?.playback_volume || 0.85;

    try {
      const data = await api.getPoolClip(moduleId);

      if (!data.success || !data.audio) {
        logger.warn(`No clips available for ambient ${moduleId}`);
        this._playing = false;
        return false;
      }

      // Re-check suppression after async fetch
      if (this._suppressed) {
        logger.debug(`Ambient skipped ${moduleId} (suppressed after fetch)`);
        this._playing = false;
        return false;
      }

      // Duck music volume before playing clip (await ensures music is ducked before clip starts)
      if (this._fadeVolume) await this._fadeVolume(0.3, 200);

      this._audioElement = new Audio(`data:audio/mpeg;base64,${data.audio}`);
      this._audioElement.volume = clipVolume;

      this._audioElement.onended = () => {
        this._lastPlayEndTime = Date.now();
        this._audioElement = null;
        this._playing = false;
        // Restore music volume after clip ends
        if (this._fadeVolume) this._fadeVolume(1.0, 800);
      };

      this._audioElement.onerror = () => {
        logger.warn(`Ambient clip error for ${moduleId}`);
        this._audioElement = null;
        this._playing = false;
        if (this._fadeVolume) this._fadeVolume(1.0, 300);
      };

      await this._audioElement.play();
      logger.platform(`Playing ambient: ${moduleId} (vol=${clipVolume}, clip=${data.clip})`);
      return true;

    } catch (error) {
      logger.error(`Ambient clip fetch failed for ${moduleId}:`, error);
      this._playing = false;
      if (this._fadeVolume) this._fadeVolume(1.0, 300);
      return false;
    }
  }
}

// Singleton instance
const ambientAudioManager = new AmbientAudioManager();
export default ambientAudioManager;
